/**
 * Component variants (SPEC §6.2). Every class here references ONLY token-based
 * utilities from theme/index.css — never a Tailwind palette, numeric spacing
 * or font size. Screens never import this file; they compose `src/web/ui`.
 */

export const THEMES = ["default", "minimal"] as const;
export type ThemeName = (typeof THEMES)[number];

export const SCHEMES = ["system", "light", "dark"] as const;
export type SchemeName = (typeof SCHEMES)[number];

/** Select a named theme for the whole app (sets `data-theme` on <html>). */
export function setTheme(name: ThemeName): void {
  document.documentElement.dataset.theme = name;
}

/** Force light/dark for the whole app, or `system` to follow the OS. */
export function setScheme(scheme: SchemeName): void {
  if (scheme === "system") delete document.documentElement.dataset.scheme;
  else document.documentElement.dataset.scheme = scheme;
}

/** Keep text readable on user-selected venue colors, independently of the app theme. */
export function venueTextColor(color?: string | null): string {
  const hex = color?.replace(/^#/, "");
  if (!hex || !/^(?:[\da-f]{3}|[\da-f]{6})$/i.test(hex)) return "var(--accent-contrast)";
  const full = hex.length === 3 ? [...hex].map((digit) => digit.repeat(2)).join("") : hex;
  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
  });
  const luminance = channels[0] * .2126 + channels[1] * .7152 + channels[2] * .0722;
  return (luminance + .05) / .05 >= 1.05 / (luminance + .05) ? "#000000" : "#ffffff";
}

export const tap = "min-h-tap min-w-tap";
export const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg";
export const pressable = "transition active:opacity-70 disabled:opacity-50 disabled:pointer-events-none";

export const button = {
  base: `inline-flex items-center justify-center gap-2 px-4 rounded-control text-headline select-none ${tap} ${focusRing} ${pressable}`,
  variant: {
    primary: "bg-accent text-accent-contrast",
    secondary: "bg-surface text-accent hairline border-border",
    destructive: "bg-danger text-accent-contrast",
  },
  fullWidth: "w-full",
} as const;
export type ButtonVariant = keyof typeof button.variant;

export const chip = {
  base: "inline-flex items-center gap-1 px-2 min-h-6 rounded-chip text-caption font-semibold whitespace-nowrap",
  tone: {
    neutral: "bg-surface-2 text-text-2",
    success: "bg-success/10 text-success",
    warn: "bg-warn/20 text-text",
    danger: "bg-danger/10 text-danger",
    accent: "bg-accent/10 text-accent",
  },
} as const;
export type ChipTone = keyof typeof chip.tone;

export const text = {
  variant: {
    "large-title": "text-large-title",
    title: "text-title",
    headline: "text-headline",
    body: "text-body",
    callout: "text-callout",
    caption: "text-caption",
  },
  tone: {
    default: "text-text",
    secondary: "text-text-2",
    tertiary: "text-text-3",
    accent: "text-accent",
    danger: "text-danger",
  },
  tabular: "tabular",
} as const;
export type TextVariant = keyof typeof text.variant;
export type TextTone = keyof typeof text.tone;

export const card = "v365-card bg-surface rounded-card shadow-card p-4";

export const row = {
  base: `v365-row flex items-center gap-3 w-full text-left px-4 py-3 ${tap} bg-surface`,
  interactive: `${focusRing} ${pressable} cursor-pointer`,
  divider: "[border-bottom-width:var(--border-width)] border-border last:[border-bottom-width:0]",
  icon: "shrink-0 text-accent",
  trailing: "v365-row-trailing shrink-0 flex items-center gap-1 text-text-3 max-w-[60%]",
  /** Row with quick actions: the main part is the button, the actions sit beside it. */
  split: "flex items-stretch w-full bg-surface",
  main: "flex-1 min-w-0",
  actions: "shrink-0 flex items-center pr-2",
} as const;

export const navBar = {
  base: "sticky top-0 z-10 bg-bg/90 backdrop-blur pt-safe-top",
  inner: `flex items-center gap-2 px-2 ${tap} max-w-content mx-auto w-full`,
  title: "flex-1 truncate text-headline text-center",
  action: `inline-flex items-center justify-center rounded-control text-accent ${tap} ${focusRing} ${pressable}`,
} as const;

export const tabBar = {
  base: "sticky bottom-0 z-10 bg-surface/95 backdrop-blur hairline border-t border-border pb-safe-bottom",
  inner: "flex max-w-content mx-auto w-full",
  item: `flex-1 flex flex-col items-center justify-center gap-1 py-1 ${tap} text-caption ${focusRing} ${pressable}`,
  active: "text-accent",
  inactive: "text-text-3",
  /** A count on a tab: how many things are waiting behind it. Anchored inside
   * the icon's box so a three-digit count grows without crossing the next tab. */
  icon: "relative inline-flex",
  badge: "absolute -top-1 left-4 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-chip bg-accent text-accent-contrast text-badge font-semibold leading-none",
} as const;

export const field = {
  label: "block text-callout text-text-2 mb-1",
  input: `block w-full ${tap} px-3 rounded-control bg-surface text-body text-text hairline border-border placeholder:text-text-3 ${focusRing}`,
} as const;

export const emptyState = {
  base: "flex flex-col items-center justify-center text-center gap-3 px-gutter py-8",
  icon: "text-text-3",
} as const;

export const skeleton = {
  row: "h-tap rounded-control v365-shimmer",
  card: "h-24 rounded-card v365-shimmer",
} as const;

export const spacing = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  7: "gap-7",
  8: "gap-8",
} as const;
export type Gap = keyof typeof spacing;

/** Phone-first page body; responsive.css anchors desktop staff workspaces to the left. */
/** A flex column so a docked bar (`dock`, `mt-auto`) sits on the TabBar even when the content is short. */
export const page = "flex-1 flex flex-col w-full max-w-content mx-auto px-gutter py-4";

// ---------------------------------------------------------------- Phase 1 components

/** Bottom sheet on phones; a centered panel at ≥768px. */
export const sheet = {
  backdrop: "fixed inset-0 z-20 bg-scrim",
  panel: "fixed inset-x-0 bottom-0 z-30 flex flex-col max-h-[92dvh] bg-bg rounded-t-card shadow-sheet v365-sheet-in",
  handleWrap: "flex justify-center pt-2 pb-1 md:hidden",
  handle: "w-12 h-1 rounded-chip bg-text-3/50",
  header: `flex items-center gap-2 px-2 ${tap}`,
  title: "flex-1 truncate text-headline text-center",
  close: `inline-flex items-center justify-center rounded-control text-accent ${tap} ${focusRing} ${pressable}`,
  body: "flex-1 overflow-y-auto overscroll-contain px-gutter py-3",
  footer: "shrink-0 px-gutter pt-3 pb-safe-bottom bg-bg hairline border-t border-border",
  footerInner: "pb-4",
} as const;

export const formField = {
  wrap: "flex flex-col gap-1 min-w-0",
  label: "block text-callout text-text-2",
  control: `block w-full ${tap} px-3 rounded-control bg-surface text-body text-text hairline border-border placeholder:text-text-3 ${focusRing} disabled:opacity-50`,
  textarea: "min-h-24 py-2 leading-normal",
  selectWrap: "relative",
  selectIcon: "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-3",
  select: "appearance-none pr-8",
  prefix: "absolute left-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none",
  withPrefix: "pl-7",
} as const;

export const segmented = {
  base: "v365-segmented flex w-full p-1 rounded-control bg-surface-2 gap-1",
  item: `flex-1 min-w-0 truncate rounded-control px-1 py-1 min-h-tap text-callout text-center ${focusRing} ${pressable}`,
  active: "bg-surface text-text shadow-card font-semibold",
  inactive: "text-text-2",
} as const;

export const searchBar = {
  wrap: `relative flex items-center ${tap}`,
  icon: "pointer-events-none absolute left-3 text-text-3",
  input: `block w-full ${tap} pl-12 pr-12 rounded-control bg-surface-2 text-body text-text placeholder:text-text-3 ${focusRing}`,
  clear: `absolute right-0 inline-flex items-center justify-center text-text-3 ${tap} ${focusRing} ${pressable}`,
} as const;

export const toast = {
  viewport: "v365-toast-viewport pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col items-center gap-2 px-gutter pb-safe-bottom",
  offset: "mb-24",
  base: "v365-toast pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-card shadow-sheet text-callout max-w-content w-full v365-sheet-in",
  tone: {
    neutral: "bg-text text-bg",
    success: "bg-success text-accent-contrast",
    danger: "bg-danger text-accent-contrast",
  },
} as const;
export type ToastTone = keyof typeof toast.tone;

export const avatar = {
  base: "v365-avatar inline-flex items-center justify-center shrink-0 rounded-chip bg-accent/10 text-accent font-semibold select-none",
  size: { sm: "size-8 text-caption", md: "size-12 text-headline" },
  ring: "v365-ring",
} as const;
export type AvatarSize = keyof typeof avatar.size;

export const timeline = {
  item: "flex gap-3",
  rail: "flex flex-col items-center shrink-0",
  dot: "inline-flex items-center justify-center size-8 rounded-chip bg-surface-2 text-accent",
  line: "flex-1 w-px bg-border my-1",
  body: "flex-1 min-w-0 flex flex-col gap-1 pb-4",
} as const;

export const toggle = {
  row: `flex items-center justify-between gap-3 w-full text-left ${tap} ${focusRing}`,
  bare: `inline-flex items-center justify-center shrink-0 ${tap} ${focusRing}`,
  track: "relative shrink-0 w-12 h-7 rounded-chip transition",
  on: "bg-success",
  off: "bg-text-3/40",
  knob: "absolute top-[2px] left-[2px] size-6 rounded-chip bg-surface shadow-card transition",
  knobOn: "translate-x-5",
} as const;

export const monthGrid = {
  header: "grid grid-cols-7 text-center text-caption text-text-3",
  grid: "v365-month-grid grid grid-cols-7 gap-px rounded-card overflow-hidden bg-border hairline border-border",
  cell: `v365-month-cell flex flex-col items-stretch gap-1 min-h-12 md:min-h-24 p-1 bg-surface text-left ${focusRing} ${pressable}`,
  cellMuted: "bg-surface-2",
  cellSelected: "v365-selected",
  day: "text-caption text-text-2 tabular text-center md:text-left",
  dayToday: "text-accent font-semibold",
  bar: "v365-calendar-bar h-1 md:h-5 rounded-chip w-full",
  barLabel: "v365-calendar-bar-label hidden md:block truncate text-caption text-accent-contrast px-1 leading-5",
  barGrey: "bg-text-3/40",
  dot: "size-2 rounded-chip self-center md:self-start md:ml-1",
  more: "v365-calendar-more text-caption text-text-3 tabular text-center",
} as const;

export const chipSelectable = {
  base: `${tap} shrink-0 inline-flex items-center gap-2 px-3 rounded-chip text-callout hairline whitespace-nowrap ${focusRing} ${pressable}`,
  selected: "bg-accent text-accent-contrast border-accent",
  unselected: "bg-surface text-text border-border",
  dot: "inline-block size-3 rounded-chip shrink-0 v365-bar",
} as const;

export const stat = {
  row: "v365-stat-row flex flex-wrap gap-2 md:gap-3",
  card: "v365-stat bg-surface rounded-card shadow-card p-3 md:p-4 flex flex-col gap-1",
  label: "text-caption text-text-2",
  value: "v365-stat-value text-callout font-semibold md:text-title tabular",
} as const;

export const lineRow = {
  amount: "text-body tabular text-text shrink-0",
  remove: `inline-flex items-center justify-center text-danger rounded-control ${tap} ${focusRing} ${pressable}`,
} as const;

/** A horizontally scrolling strip (chips, day headers). */
export const scroller = "v365-chip-strip flex gap-2 overflow-x-auto -mx-gutter px-gutter pb-1 [scrollbar-width:none]";

export const sectionTitle = "text-caption text-text-3 uppercase tracking-wide px-gutter";

// ---------------------------------------------------------------- Phase 2 components

/** Sticky surface pinned above the TabBar (lead action bar, message composer); bleeds into the page gutters. */
export const dock = "v365-dock sticky z-10 mt-auto bottom-[calc(var(--tabbar-h)+var(--safe-bottom))] -mx-gutter -mb-4 px-gutter py-2 bg-bg/95 backdrop-blur hairline border-t border-border";

export const actionBar = {
  base: "v365-action-bar flex justify-between gap-1 max-w-content mx-auto",
  item: `flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-1 ${tap} rounded-control text-caption text-accent ${focusRing} ${pressable}`,
  label: "truncate max-w-full",
} as const;

/** Conversation bubbles: inbound left on surface, outbound right in accent; a caption line under each. */
export const bubble = {
  wrap: { in: "flex flex-col items-start gap-1", out: "flex flex-col items-end gap-1" },
  base: "v365-bubble max-w-[85%] px-3 py-2 rounded-card text-body whitespace-pre-wrap break-words [overflow-wrap:anywhere]",
  tone: { in: "bg-surface text-text shadow-card", out: "bg-accent text-accent-contrast" },
  caption: "text-caption text-text-3 px-1",
  system: "text-caption text-text-3 text-center py-1",
} as const;
export type BubbleDirection = keyof typeof bubble.tone;
